import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CACHE_KEYS, isCacheValid } from '@/lib/cacheUtils';

export interface NavVisibility {
  build: boolean;
  gallery: boolean;
  myGear: boolean;
  docs: boolean;
}

export interface SiteSettings {
  id: string;
  site_name: string;
  logo_url: string | null;
  nav_visibility: NavVisibility;
  google_analytics_id: string | null;
  google_analytics_enabled: boolean;
  // SEO fields
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  seo_og_image: string | null;
  seo_twitter_handle: string;
  canonical_domain: string;
  microsoft_clarity_id: string | null;
  microsoft_clarity_enabled: boolean;
}

const CACHE_TTL = 1000 * 60 * 60; // 1 hour

interface CachedSiteSettings {
  data: SiteSettings;
  timestamp: number;
}

const DEFAULT_SETTINGS: SiteSettings = {
  id: '',
  site_name: 'CineRig Pro',
  logo_url: null,
  nav_visibility: {
    build: true,
    gallery: true,
    myGear: true,
    docs: true,
  },
  google_analytics_id: null,
  google_analytics_enabled: true,
  // SEO defaults
  seo_title: 'CineRig Pro - Professional Camera Rig Configuration',
  seo_description: 'Build your perfect camera rig with intelligent compatibility checking, power analysis, and weight optimization for cinema professionals.',
  seo_keywords: 'camera rig, cinema, RED, ARRI, Sony, Blackmagic, cage, accessories, filmmaking',
  seo_og_image: null,
  seo_twitter_handle: '@CineRigPro',
  canonical_domain: 'https://cinerig.pro',
  microsoft_clarity_id: null,
  microsoft_clarity_enabled: true,
};

const getCachedSettings = (): SiteSettings | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.SITE_SETTINGS);
    if (cached) {
      const parsed: CachedSiteSettings = JSON.parse(cached);
      if (isCacheValid(parsed.timestamp, CACHE_TTL)) {
        return parsed.data;
      }
    }
    return null;
  } catch {
    return null;
  }
};

const setCachedSettings = (settings: SiteSettings) => {
  try {
    const cached: CachedSiteSettings = { data: settings, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEYS.SITE_SETTINGS, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
};

export const clearSiteSettingsCache = () => {
  try {
    localStorage.removeItem(CACHE_KEYS.SITE_SETTINGS);
  } catch {
    // Ignore storage errors
  }
};

// Module-level dedup for concurrent hook instances
let siteSettingsFetchPromise: Promise<void> | null = null;
let siteSettingsResolved: SiteSettings | null = null;

export function useSiteSettings() {
  const cached = getCachedSettings();
  const [settings, setSettings] = useState<SiteSettings>(cached || siteSettingsResolved || DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(!cached && !siteSettingsResolved);

  useEffect(() => {
    // If we already have cached or resolved settings, skip the fetch
    if (cached || siteSettingsResolved) {
      setIsLoading(false);
      return;
    }

    const runFetch = async () => {
      // Dedup: if a fetch is already in flight, wait for it
      if (!siteSettingsFetchPromise) {
        siteSettingsFetchPromise = (async () => {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            
            const table = session ? 'site_settings' : 'public_site_settings';
            const { data, error } = await supabase
              .from(table)
              .select('*')
              .limit(1)
              .maybeSingle();

            if (error || !data) return;

            const newSettings: SiteSettings = {
              id: data.id,
              site_name: data.site_name,
              logo_url: data.logo_url,
              nav_visibility: data.nav_visibility as unknown as NavVisibility,
              google_analytics_id: data.google_analytics_id || null,
              google_analytics_enabled: data.google_analytics_enabled ?? true,
              seo_title: data.seo_title || DEFAULT_SETTINGS.seo_title,
              seo_description: data.seo_description || DEFAULT_SETTINGS.seo_description,
              seo_keywords: data.seo_keywords || DEFAULT_SETTINGS.seo_keywords,
              seo_og_image: data.seo_og_image || null,
              seo_twitter_handle: data.seo_twitter_handle || DEFAULT_SETTINGS.seo_twitter_handle,
              canonical_domain: data.canonical_domain || DEFAULT_SETTINGS.canonical_domain,
              microsoft_clarity_id: data.microsoft_clarity_id || null,
              microsoft_clarity_enabled: data.microsoft_clarity_enabled ?? true,
            };
            siteSettingsResolved = newSettings;
            setCachedSettings(newSettings);
          } catch (error) {
            console.error('Error fetching site settings:', error);
          } finally {
            siteSettingsFetchPromise = null;
          }
        })();
      }

      await siteSettingsFetchPromise;
      if (siteSettingsResolved) {
        setSettings(siteSettingsResolved);
      }
      setIsLoading(false);
    };

    runFetch();

    // Subscribe to changes
    const channel = supabase
      .channel('site_settings_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'site_settings' },
        (payload) => {
          if (payload.new) {
            const data = payload.new as any;
            const newSettings: SiteSettings = {
              id: data.id,
              site_name: data.site_name,
              logo_url: data.logo_url,
              nav_visibility: data.nav_visibility as unknown as NavVisibility,
              google_analytics_id: data.google_analytics_id || null,
              google_analytics_enabled: data.google_analytics_enabled ?? true,
              // SEO fields
              seo_title: data.seo_title || DEFAULT_SETTINGS.seo_title,
              seo_description: data.seo_description || DEFAULT_SETTINGS.seo_description,
              seo_keywords: data.seo_keywords || DEFAULT_SETTINGS.seo_keywords,
              seo_og_image: data.seo_og_image || null,
              seo_twitter_handle: data.seo_twitter_handle || DEFAULT_SETTINGS.seo_twitter_handle,
              canonical_domain: data.canonical_domain || DEFAULT_SETTINGS.canonical_domain,
              microsoft_clarity_id: data.microsoft_clarity_id || null,
              microsoft_clarity_enabled: data.microsoft_clarity_enabled ?? true,
            };
            setSettings(newSettings);
            setCachedSettings(newSettings);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { settings, isLoading };
}
